import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@mavula.com");
  const [password, setPassword] = useState("demo1234");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login.welcome_toast", "Welcome back to MAVULA"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="mv-eyebrow"><span style={{color:"var(--mv-gold)"}}>{t("login.eyebrow")}</span></div>
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl mv-green-text mt-3 leading-tight">
          {t("login.title")} <span className="font-accent italic" style={{color:"var(--mv-gold)"}}>{t("login.italic")}</span>
        </h1>
        <p className="mt-4 text-[15px] max-w-md" style={{color:"var(--mv-muted)"}}>
          {t("login.sub", "See balance, monthly payments, property progress and receipts — private, secure, only for you.")}
        </p>
        <div className="mt-6 mv-card p-4 text-[13px]" style={{color:"var(--mv-muted)"}}>
          <div className="text-[11px] uppercase tracking-[0.2em] mb-1" style={{color:"var(--mv-gold)"}}>{t("login.demo", "Demo Account")}</div>
          Email <span className="mv-green-text font-semibold">demo@mavula.com</span> · Password <span className="mv-green-text font-semibold">demo1234</span>
        </div>
      </div>

      <form onSubmit={submit} className="mv-card p-6 md:p-8" data-testid="login-form">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 mv-green-text" style={{background:"rgba(212,175,55,0.16)"}}>
          <KeyRound size={22}/>
        </div>
        <h2 className="font-display text-2xl mv-green-text">{t("login.submit")}</h2>
        <div className="mt-6 space-y-4">
          <label className="block">
            <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{color:"var(--mv-muted)"}}>{t("login.email")}</span>
            <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border mv-hairline"/>
          </label>
          <label className="block">
            <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{color:"var(--mv-muted)"}}>{t("login.password")}</span>
            <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border mv-hairline"/>
          </label>
        </div>
        <button type="submit" disabled={loading} className="btn-gold mt-6 w-full justify-center disabled:opacity-60" data-testid="login-submit">
          {loading ? t("login.signing") : (<>{t("login.submit")} <ArrowRight size={16}/></>)}
        </button>
        <div className="mt-3 flex flex-wrap justify-between gap-2 text-[13px]">
          <Link to="/forgot-password" className="mv-green-text underline" data-testid="login-forgot-link">Forgot password?</Link>
          <span style={{color:"var(--mv-muted)"}}>
            {t("login.no_account", "Not registered yet?")} <Link to="/register-interest" className="mv-green-text underline">{t("login.open_account_link", "Open an account")}</Link>
          </span>
        </div>
      </form>
    </div>
  );
}
